import { ComponentType, Suspense, useContext, useEffect, useMemo, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import cookie from 'js-cookie';
import Login from '../Login/Login';
import { SessionContext } from '../../auth/SessionProvider';
import NavigationLayout from 'components/ui/NavigationLayout/NavigationLayout';
import { ErrorHandler } from 'auth/ErrorHandler';
import EmailSentPage from 'components/EmailSentPage/EmailSentPage';
import AppLoader from 'components/AppLoader/AppLoader';
import { PageSkeleton } from 'components/PageSkeleton/PageSkeleton';
import NoAdminAccessPage from 'components/ErrorPages/NoAdminAccess/NoAdminAccessPage';
import { lacksAdminAccess } from 'helpers/sessionAccess';
import { isJsonString } from 'utils/helpers';
import { CliDockHost } from 'components/Cli/CliDock';
import { useAccess } from 'hooks/useAccess';
import {
  ADMIN_PAGES,
  AdminPage,
  canOpen,
  skeletonAt,
} from 'navigation/adminPages';

const Home = lazy(() => import('components/Home/Home'));

function ConsumptionLandingRedirect() {
  const { search } = useLocation();
  const view = new URLSearchParams(search).get('view');
  if (
    view === 'ipu' ||
    view === 'storage' ||
    view === 'ai' ||
    view === 'burn'
  ) {
    return (
      <Navigate to={`/consumption/analytics-and-charts${search}`} replace />
    );
  }
  return <Navigate to="/consumption/consumption-records" replace />;
}

// The schedules page became a drawer on the dashboards page; old links still land on the same customer.
function DashboardSchedulesRedirect() {
  const params = new URLSearchParams(useLocation().search);
  const find = params.get('q');
  params.delete('q');
  if (find) params.set('find', find);
  params.set('schedules', '1');
  return <Navigate to={`/consumption/dashboards?${params}`} replace />;
}

function AppRoutes() {
  const { user, isAuthenticated, isLoading, error } =
    useContext(SessionContext);
  const { canAny, isLoaded: isAccessLoaded } = useAccess();
  const { pathname } = useLocation();

  useEffect(() => {
    let script = document.createElement('script');
    if (pathname === '/signup' || pathname === '/signup/') {
      script = document.createElement('script');
      script.src = '//js.hs-scripts.com/7507810.js';
      script.async = true;
      script.defer = true;
      script.id = 'hs-script-loader';
      document.body.appendChild(script);
      document
        .querySelectorAll('.hs-cookie-notification-position-bottom')
        .forEach((e: any) => (e.style.display = 'block'));
    } else {
      document
        .querySelectorAll('.hs-cookie-notification-position-bottom')
        .forEach((e: any) => (e.style.display = 'none'));
    }
  });

  function getCookie(cname: string) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return '';
  }

  const isLoggedIn = !!getCookie('CloudAdminSSO');

  // Auth0 finished loading but session is gone — stale cookie is blocking the login page.
  // Clear it so the user gets redirected to login instead of being stuck on the skeleton.
  if (!isLoading && !isAuthenticated && isLoggedIn) {
    cookie.remove('CloudAdminSSO');
    return <Login />;
  }

  if (error) {
    if (isJsonString(error.message)) {
      const { message, userEmail } = JSON.parse(error.message);
      return <ErrorHandler message={message as string} userEmail={userEmail} />;
    } else {
      return <ErrorHandler message={error.message} />;
    }
  }

  if (!isAuthenticated && !isLoggedIn) {
    return <Login />;
  }

  if (!user?.userDataIsSet) {
    return <AppLoader />;
  }

  if (user?.email && isAuthenticated) {
    if (!user?.confirmed) {
      return <ErrorHandler message={'Verify Your Email'} />;
    } else if (
      lacksAdminAccess({
        roles: user.roles,
        permissions: user.access?.permissions,
      })
    ) {
      return <NoAdminAccessPage />;
    }
  }

  if (isLoading || !isAccessLoaded) {
    return <AppLoader />;
  }

  const pageRoute = (page: AdminPage) => (
    <Route
      key={page.path}
      path={page.path}
      element={
        <Suspense fallback={<PageSkeleton kind={page.skeleton} />}>
          <ProtectedRoute
            component={page.component}
            isPermitted={canOpen(page, canAny)}
          />
        </Suspense>
      }
    />
  );

  // Every other page sits in the app's frame: the sidebar and the content area.
  const framed = (
    <NavigationLayout>
      <Suspense fallback={<PageSkeleton kind={skeletonAt(pathname)} />}>
        <Routes>
          <Route path="/" element={<ProtectedRoute component={Home} />} />
          {ADMIN_PAGES.filter(page => !page.fullScreen).map(pageRoute)}
          <Route path="/verify" element={<EmailSentPage />} />
          <Route path="/consumption" element={<ConsumptionLandingRedirect />} />
          <Route
            path="/consumption/dashboard-schedules"
            element={<DashboardSchedulesRedirect />}
          />

          <Route
            path="/billing"
            element={<Navigate to="/billing/finops/gcp" replace />}
          />
          <Route
            path="/billing/gcp"
            element={<Navigate to="/billing/finops/gcp" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </NavigationLayout>
  );

  // The dock sits beside every route, framed or not, so a session outlives any page change.
  return (
    <>
      <Routes>
        {/* A cluster's infrastructure map is a page of its own that takes the whole screen, outside the
            app's frame; it opens from the cluster's page. */}
        {ADMIN_PAGES.filter(page => page.fullScreen).map(pageRoute)}
        <Route path="*" element={framed} />
      </Routes>
      <CliDockHost />
    </>
  );
}

interface ProtectedRouteProps {
  component: ComponentType<any>;
  isPermitted?: boolean;
  redirectURL?: string;
}

function ProtectedRoute({
  component,
  isPermitted = true,
  redirectURL = '/',
}: ProtectedRouteProps) {
  const location = useLocation();

  // Wrapped once per page and path: a new wrapper on every render is a new component type, so each
  // query-string change (a filter, a selection) would remount the page and throw its state away.
  const AuthenticatedComponent = useMemo(
    () =>
      withAuthenticationRequired(component, {
        onRedirecting: () => <AppLoader />,
        returnTo: location.pathname || '/',
        loginOptions: {
          redirectUri: window.location.origin,
          appState: {
            returnTo: location.pathname,
          },
        },
      }),
    [component, location.pathname],
  );

  if (!isPermitted) return <Navigate to={redirectURL} replace />;

  return <AuthenticatedComponent />;
}

export default AppRoutes;
